/**
 * TAHLEEL.ai LLM Provider Registry
 *
 * All supported LLM providers for tactical analysis.
 */

export const LLM_PROVIDERS = [
  {
    id: 'anthropic',
    name: 'Anthropic',
    model: 'claude-sonnet-4-5-20250929',
    recommended: true,
    badge: 'Recommended',
    description: 'Claude Sonnet 4.5 — best-in-class reasoning for tactical football analysis. Preferred by TAHLEEL.ai.',
    apiKeyPrefix: 'sk-ant-',
    docsUrl: 'https://console.anthropic.com/settings/keys',
    icon: 'A',
    color: '#d4a574'
  },
  {
    id: 'openai',
    name: 'OpenAI',
    model: 'gpt-4o',
    recommended: false,
    badge: 'Popular',
    description: 'GPT-4o — strong multimodal model with fast inference and broad capabilities.',
    apiKeyPrefix: 'sk-',
    docsUrl: 'https://platform.openai.com/api-keys',
    icon: 'O',
    color: '#10a37f'
  },
  {
    id: 'google',
    name: 'Google Gemini',
    model: 'gemini-2.0-flash',
    recommended: false,
    badge: null,
    description: 'Gemini 2.0 Flash — Google\'s fast and capable model with long context support.',
    apiKeyPrefix: 'AI',
    docsUrl: 'https://aistudio.google.com/app/apikey',
    icon: 'G',
    color: '#4285f4'
  },
  {
    id: 'mistral',
    name: 'Mistral AI',
    model: 'mistral-large-latest',
    recommended: false,
    badge: null,
    description: 'Mistral Large — European AI with strong multilingual and reasoning capabilities.',
    apiKeyPrefix: '',
    docsUrl: 'https://console.mistral.ai/api-keys/',
    icon: 'M',
    color: '#ff7000'
  },
  {
    id: 'meta',
    name: 'Meta Llama',
    model: 'llama-3.1-405b',
    recommended: false,
    badge: 'Open Source',
    description: 'Llama 3.1 405B — Meta\'s largest open-source model. Requires compatible API host.',
    apiKeyPrefix: '',
    docsUrl: 'https://llama.meta.com/',
    icon: 'L',
    color: '#0668e1'
  },
  {
    id: 'cohere',
    name: 'Cohere',
    model: 'command-r-plus',
    recommended: false,
    badge: null,
    description: 'Command R+ — enterprise-focused model with strong RAG and multilingual support.',
    apiKeyPrefix: '',
    docsUrl: 'https://dashboard.cohere.com/api-keys',
    icon: 'C',
    color: '#39594d'
  },
  {
    id: 'perplexity',
    name: 'Perplexity',
    model: 'llama-3.1-sonar-huge-128k-online',
    recommended: false,
    badge: null,
    description: 'Sonar Huge — online model with real-time web access for up-to-date tactical research.',
    apiKeyPrefix: 'pplx-',
    docsUrl: 'https://www.perplexity.ai/settings/api',
    icon: 'P',
    color: '#20808d'
  },
  {
    id: 'groq',
    name: 'Groq',
    model: 'llama-3.3-70b-versatile',
    recommended: false,
    badge: 'Fastest',
    description: 'Groq LPU inference — extremely fast response times for real-time tactical queries.',
    apiKeyPrefix: 'gsk_',
    docsUrl: 'https://console.groq.com/keys',
    icon: 'Q',
    color: '#f55036'
  },
  {
    id: 'together',
    name: 'Together AI',
    model: 'meta-llama/Meta-Llama-3.1-405B-Instruct-Turbo',
    recommended: false,
    badge: null,
    description: 'Together AI — run open-source models at scale with serverless inference.',
    apiKeyPrefix: '',
    docsUrl: 'https://api.together.xyz/settings/api-keys',
    icon: 'T',
    color: '#0f6fff'
  },
  {
    id: 'fireworks',
    name: 'Fireworks AI',
    model: 'accounts/fireworks/models/llama-v3p1-405b-instruct',
    recommended: false,
    badge: null,
    description: 'Fireworks — fast open-source model hosting with competitive pricing.',
    apiKeyPrefix: 'fw_',
    docsUrl: 'https://fireworks.ai/account/api-keys',
    icon: 'F',
    color: '#ff6b2b'
  },
  {
    id: 'deepseek',
    name: 'DeepSeek',
    model: 'deepseek-chat',
    recommended: false,
    badge: 'Budget',
    description: 'DeepSeek V2 — cost-effective model with strong coding and reasoning abilities.',
    apiKeyPrefix: 'sk-',
    docsUrl: 'https://platform.deepseek.com/api_keys',
    icon: 'D',
    color: '#4d6bfe'
  },
  {
    id: 'xai',
    name: 'xAI Grok',
    model: 'grok-2',
    recommended: false,
    badge: null,
    description: 'Grok 2 — xAI\'s conversational model with real-time knowledge.',
    apiKeyPrefix: 'xai-',
    docsUrl: 'https://console.x.ai/',
    icon: 'X',
    color: '#000000'
  },
  {
    id: 'ai21',
    name: 'AI21 Labs',
    model: 'jamba-1.5-large',
    recommended: false,
    badge: null,
    description: 'Jamba 1.5 Large — hybrid SSM-Transformer architecture with 256K context window.',
    apiKeyPrefix: '',
    docsUrl: 'https://studio.ai21.com/account/api-key',
    icon: '21',
    color: '#6c3baa'
  },
  {
    id: 'sambanova',
    name: 'SambaNova',
    model: 'Meta-Llama-3.1-405B-Instruct',
    recommended: false,
    badge: null,
    description: 'SambaNova Cloud — enterprise AI platform with custom silicon for fast inference.',
    apiKeyPrefix: '',
    docsUrl: 'https://cloud.sambanova.ai/apis',
    icon: 'S',
    color: '#f04e23'
  },
  {
    id: 'azure',
    name: 'Azure OpenAI',
    model: 'gpt-4o (Azure)',
    recommended: false,
    badge: 'Enterprise',
    description: 'Azure-hosted OpenAI — enterprise compliance, data residency, and managed deployment.',
    apiKeyPrefix: '',
    docsUrl: 'https://portal.azure.com/#view/Microsoft_Azure_AI/AzureOpenAI',
    icon: 'Az',
    color: '#0078d4'
  },
  {
    id: 'aws_bedrock',
    name: 'AWS Bedrock',
    model: 'anthropic.claude-sonnet-4-5-20250929-v1:0',
    recommended: false,
    badge: 'Enterprise',
    description: 'AWS Bedrock — access Claude and other models through your AWS account with VPC security.',
    apiKeyPrefix: '',
    docsUrl: 'https://console.aws.amazon.com/bedrock/',
    icon: 'B',
    color: '#ff9900'
  },
  {
    id: 'anyscale',
    name: 'Anyscale',
    model: 'meta-llama/Meta-Llama-3.1-70B-Instruct',
    recommended: false,
    badge: null,
    description: 'Anyscale Endpoints — scalable open-source model serving built on Ray.',
    apiKeyPrefix: 'esecret_',
    docsUrl: 'https://app.endpoints.anyscale.com/',
    icon: 'An',
    color: '#0071e3'
  },
  {
    id: 'replicate',
    name: 'Replicate',
    model: 'meta/meta-llama-3.1-405b-instruct',
    recommended: false,
    badge: null,
    description: 'Replicate — run open-source models in the cloud with simple API calls.',
    apiKeyPrefix: 'r8_',
    docsUrl: 'https://replicate.com/account/api-tokens',
    icon: 'R',
    color: '#3d3d3d'
  },
  {
    id: 'openrouter',
    name: 'OpenRouter',
    model: 'anthropic/claude-sonnet-4-5-20250929',
    recommended: false,
    badge: 'Multi-Provider',
    description: 'OpenRouter — unified API gateway to 100+ models. Route to the best model automatically.',
    apiKeyPrefix: 'sk-or-',
    docsUrl: 'https://openrouter.ai/keys',
    icon: 'OR',
    color: '#6366f1'
  },
  {
    id: 'huggingface',
    name: 'Hugging Face',
    model: 'meta-llama/Meta-Llama-3.1-405B-Instruct',
    recommended: false,
    badge: 'Open Source',
    description: 'Hugging Face Inference API — access thousands of open-source models.',
    apiKeyPrefix: 'hf_',
    docsUrl: 'https://huggingface.co/settings/tokens',
    icon: 'HF',
    color: '#ffcc00'
  }
];

/**
 * Get a provider by its ID
 */
export const getProviderById = (id) => {
  return LLM_PROVIDERS.find(p => p.id === id) || null;
};

/**
 * Get the recommended provider
 */
export const getRecommendedProvider = () => {
  return LLM_PROVIDERS.find(p => p.recommended) || LLM_PROVIDERS[0];
};

export default LLM_PROVIDERS;
